import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { CheckCircle, ArrowLeft, ArrowRight, Users, Shield, TrendingUp, MessageSquare } from 'lucide-react';
import PublicNav from '../components/PublicNav';
import Footer from '../components/Footer';
import WaitlistCTA from '../components/WaitlistCTA';

export default function ForFounders() {
  const navigate = useNavigate();

  const goToWaitlist = (role) => {
    navigate('/?scroll=waitlist&role=' + role);
  };

  return (
    <div className="min-h-screen bg-slate-100" data-testid="for-founders-page">
      <PublicNav currentPage="for-founders" />

      <div className="max-w-6xl mx-auto px-6 py-20">
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-2 bg-blue-600/10 text-blue-500 rounded-full text-sm font-medium mb-6">
            FOR FOUNDERS
          </div>
          <h1 className="text-4xl font-bold text-[#1e3a5f] mb-6">
            Find investors who share<br />your funding philosophy.
          </h1>
          <p className="text-lg text-[#334d6e] max-w-3xl mx-auto">
            Connect with investors aligned with equity-based, risk-sharing capital partnerships. Protect your ideas with NDA-gated sharing. Build your team with values-aligned co-founders.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-16">
          <Card className="bg-white border-slate-200 p-8">
            <Users className="text-blue-600 mb-4" size={28} />
            <h3 className="text-xl font-semibold text-[#1e3a5f] mb-4">Values-Aligned Investors</h3>
            <p className="text-[#334d6e] mb-4 text-sm">Discover investors who prefer equity-based partnerships:</p>
            <ul className="space-y-2">
              <li className="flex items-start gap-3">
                <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                <span className="text-[#4a6382] text-sm">Active in your industry and stage</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                <span className="text-[#4a6382] text-sm">Capital aligned with your requirements</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                <span className="text-[#4a6382] text-sm">Shared funding philosophy preferences</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                <span className="text-[#4a6382] text-sm">Geographic alignment</span>
              </li>
            </ul>
          </Card>

          <Card className="bg-white border-slate-200 p-8">
            <TrendingUp className="text-blue-600 mb-4" size={28} />
            <h3 className="text-xl font-semibold text-[#1e3a5f] mb-4">Build Your Team First</h3>
            <p className="text-[#334d6e] mb-4 text-sm">Strengthen your founding team before capital conversations:</p>
            <ul className="space-y-2">
              <li className="flex items-start gap-3">
                <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                <span className="text-[#4a6382] text-sm">Find technical or business co-founders</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                <span className="text-[#4a6382] text-sm">Evaluate candidates based on skills</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                <span className="text-[#4a6382] text-sm">Present complete founding team</span>
              </li>
            </ul>
          </Card>

          <Card className="bg-white border-slate-200 p-8">
            <MessageSquare className="text-blue-600 mb-4" size={28} />
            <h3 className="text-xl font-semibold text-[#1e3a5f] mb-4">Structured Investment Conversations</h3>
            <p className="text-[#334d6e] mb-4 text-sm">Move beyond cold outreach:</p>
            <ul className="space-y-2">
              <li className="flex items-start gap-3">
                <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                <span className="text-[#4a6382] text-sm">Investors see alignment indicators</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                <span className="text-[#4a6382] text-sm">Mutual interest creates matches</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                <span className="text-[#4a6382] text-sm">Direct messaging with context</span>
              </li>
            </ul>
          </Card>

          <Card className="bg-white border-slate-200 p-8">
            <Shield className="text-blue-600 mb-4" size={28} />
            <h3 className="text-xl font-semibold text-[#1e3a5f] mb-4">Protected Information Sharing</h3>
            <p className="text-[#334d6e] mb-4 text-sm">Your business details are valuable:</p>
            <ul className="space-y-2">
              <li className="flex items-start gap-3">
                <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                <span className="text-[#4a6382] text-sm">Pitch decks locked behind NDA</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                <span className="text-[#4a6382] text-sm">Sensitive details NDA-gated</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                <span className="text-[#4a6382] text-sm">All NDA acceptances logged</span>
              </li>
            </ul>
          </Card>
        </div>

        <Card className="bg-white border-slate-200 p-12 text-center">
          <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">Ready to Find Aligned Investors?</h2>
          <p className="text-lg text-[#334d6e] mb-8">
            Join the waitlist and be first to know when SeedSynch opens for early access.
          </p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <WaitlistCTA 
              variant="founder"
              theme="light"
              size="default"
              onClick={() => goToWaitlist('founder')}
            />
            <Link to="/pricing">
              <Button className="btn-secondary text-base px-8 py-5">
                View Pricing
              </Button>
            </Link>
          </div>
        </Card>
      </div>

      <Footer />
    </div>
  );
}
